/**
 * @file PrescriptionModal.js
 * @version 1.7.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-17
 * @modified 2026-10-13
 * @description Modal for viewing, adding, and editing prescriptions
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import './Prescriptions.css';
import SearchableDropdown from './SearchableDropdown';
import Dropdown from './Dropdown';
import Input from './Input';
import DateInput from './DateInput';
import Textarea from './Textarea';
import AlertDialog from './AlertDialog';
import LoadingSpinner from './LoadingSpinner';
import AuditHistoryModal from './AuditHistoryModal';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateShort, formatPeriod } from '../utils/dateFormatters';
import { formatQuantity, formatCount } from '../utils/numberFormatters';
import { unitSuffixFor } from '../utils/units';

// Period boundaries are anchored on the prescription day. Period N ends the
// day before the same day-of-month N months later, clamped to month-end when
// the target month has fewer days. Matches the Postgres bucketing logic in
// prescriptionService.js.
const parseISODate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const computeEndDate = (fromDate, months) => {
  const from = parseISODate(fromDate);
  if (!from) return '';
  const targetDay = from.getDate() - 1;
  let endYear = from.getFullYear();
  let endMonth = from.getMonth() + months;
  while (endMonth > 11) { endMonth -= 12; endYear += 1; }
  if (targetDay === 0) {
    return formatISODate(new Date(endYear, endMonth, 0));
  }
  const daysInEndMonth = new Date(endYear, endMonth + 1, 0).getDate();
  return formatISODate(new Date(endYear, endMonth, Math.min(targetDay, daysInEndMonth)));
};

const inferDurationMonths = (fromISO, toISO) => {
  const from = parseISODate(fromISO);
  const to = parseISODate(toISO);
  if (!from || !to) return 3;
  for (let m = 1; m <= 3; m++) {
    if (computeEndDate(fromISO, m) === toISO) return m;
  }
  const days = Math.round((to - from) / 86400000) + 1;
  return Math.min(3, Math.max(1, Math.round(days / 30.4375)));
};

function PrescriptionModal({ prescription, mode = 'view', onClose, onSave }) {
  // The mode the modal is SHOWING, which can move from view to edit in place
  // via the header's Edit button. `mode` stays the one it was opened with, so
  // Cancel knows to go back to viewing rather than close outright.
  const [currentMode, setCurrentMode] = useState(mode);
  useEffect(() => { setCurrentMode(mode); }, [mode]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [closing, setClosing] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [viewData, setViewData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Slideout buttons
  useSlideOutButton();

  const [formData, setFormData] = useState({
    account_id: '',
    item_id: '',
    vet_reference: '',
    prescribed_tonnes: '',
    from_date: '',
    duration_months: 3,
    delivery_instructions: '',
    notes: ''
  });

  useEffect(() => {
    if (currentMode !== 'view') {
      loadCustomers();
      loadProducts();
    }

    // Prevent background scrolling
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) {
      contentWrapper.classList.add('modal-open');
    }

    // Pre-populate form if editing
    if (prescription && currentMode === 'edit') {
      const fromISO = prescription.from_date ? prescription.from_date.split('T')[0] : '';
      const toISO = prescription.to_date ? prescription.to_date.split('T')[0] : '';
      setFormData({
        account_id: prescription.account_id ? prescription.account_id.toString() : '',
        item_id: prescription.inventory_item_id ? prescription.inventory_item_id.toString() : '',
        vet_reference: prescription.vet_reference || '',
        prescribed_tonnes: prescription.prescribed_tonnes || '',
        from_date: fromISO,
        duration_months: inferDurationMonths(fromISO, toISO),
        delivery_instructions: prescription.delivery_instructions || '',
        notes: prescription.notes || ''
      });
    }

    // Load full details for view mode
    if (prescription && currentMode === 'view') {
      loadPrescriptionDetails(prescription.id);
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
  }, [prescription, currentMode]);

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCustomers(data.contacts || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/search?limit=10000&activeOnly=false`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setProducts(data.items || []);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadPrescriptionDetails = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/prescriptions/${id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setViewData(data.prescription);
      }
    } catch (error) {
      console.error('Error loading prescription details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
      onClose();
    }, 300);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.account_id) {
      setAlertDialog({ isOpen: true, message: 'Please select a customer', type: 'error' });
      return;
    }

    if (!formData.item_id) {
      setAlertDialog({ isOpen: true, message: 'Please select a product', type: 'error' });
      return;
    }

    if (!formData.prescribed_tonnes || parseFloat(formData.prescribed_tonnes) <= 0) {
      setAlertDialog({ isOpen: true, message: 'Please enter a valid tonnage greater than 0', type: 'error' });
      return;
    }

    if (!formData.from_date) {
      setAlertDialog({ isOpen: true, message: 'Please enter a prescription date', type: 'error' });
      return;
    }

    const durationMonths = Math.min(3, Math.max(1, parseInt(formData.duration_months) || 3));
    const toDate = computeEndDate(formData.from_date, durationMonths);
    if (!toDate) {
      setAlertDialog({ isOpen: true, message: 'Could not compute the prescription end date', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const selectedProduct = products.find(p => p.id.toString() === formData.item_id);
      const payload = {
        account_id: parseInt(formData.account_id),
        inventory_item_id: parseInt(formData.item_id),
        product_name: selectedProduct?.item_name || '',
        product_code: selectedProduct?.item_code || null,
        vet_reference: formData.vet_reference || null,
        prescribed_tonnes: parseFloat(formData.prescribed_tonnes),
        from_date: formData.from_date,
        to_date: toDate,
        delivery_instructions: formData.delivery_instructions || null,
        notes: formData.notes || null
      };

      const url = prescription
        ? `${API_URL}/api/prescriptions/${prescription.id}`
        : `${API_URL}/api/prescriptions`;

      const response = await fetch(url, {
        method: prescription ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setClosing(true);
        document.body.classList.remove('modal-open');
        const contentWrapper = document.querySelector('.content-wrapper');
        if (contentWrapper) {
          contentWrapper.classList.remove('modal-open');
        }
        setTimeout(() => {
          onSave();
        }, 300);
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to save prescription', type: 'error' });
      }
    } catch (error) {
      console.error('Error saving prescription:', error);
      setAlertDialog({ isOpen: true, message: 'Error saving prescription', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusDisplay = (rx) => {
    if (!rx) return { label: '-', className: '' };
    if (rx.is_archived) return { label: 'Archived', className: 'archived' };
    if (rx.status === 'Valid') return { label: 'Valid', className: 'active' };
    return { label: 'Expired', className: 'draft' };
  };

  const getMonthStatusClass = (actual, expected) => {
    if (actual > expected) return 'over-expected';
    return 'under-expected';
  };

  const renderViewMode = () => {
    const rx = viewData || prescription;
    if (!rx) return null;

    if (loading) {
      return <LoadingSpinner />;
    }

    const statusDisplay = getStatusDisplay(rx);

    return (
      <>
        <div className="detail-section">
          <h4>Prescription Details</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Customer</span>
              <span className="detail-value">{rx.customer_name || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Account No</span>
              <span className="detail-value">{rx.account_no || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Prescription ID</span>
              <span className="detail-value">{rx.prescription_number || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Product</span>
              <span className="detail-value">{rx.product_name || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Product Code</span>
              <span className="detail-value">{rx.product_code || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Vet Reference</span>
              <span className="detail-value">{rx.vet_reference || '-'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">From</span>
              <span className="detail-value">{formatDateShort(rx.from_date)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">To</span>
              <span className="detail-value">{formatDateShort(rx.to_date)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className="detail-value">
                <span className={`status-badge ${statusDisplay.className}`}>{statusDisplay.label}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h4>Tonnage</h4>
          <div className="tonnes-display">
            <div>
              {(() => {
                const overTotal = parseFloat(rx.remaining_tonnes || 0) < 0;
                const overPeriod = (rx.monthlySummary || []).some(p => parseFloat(p.tonnes || 0) > parseFloat(p.expected || 0));
                const isOver = overTotal || overPeriod || rx.over_in_any_period === true;
                const title = overTotal
                  ? 'Customer has ordered more than the prescribed tonnage'
                  : 'Customer has ordered more than expected in at least one period';
                return (
                  <>
                    {isOver && (
                      <span
                        className="material-symbols-rounded over-prescribed-icon"
                        style={{ color: 'var(--color-error)', fontSize: '22px', verticalAlign: 'middle', marginRight: '8px' }}
                        title={title}
                      >
                        warning
                      </span>
                    )}
                    <span className="big-number">{formatQuantity(parseFloat(rx.prescribed_tonnes || 0))}</span>
                    <span className="remaining-label"> prescribed</span>
                  </>
                );
              })()}
            </div>
            <div>
              <span className="big-number">{formatQuantity(parseFloat(rx.remaining_tonnes || 0))}</span>
              <span className="remaining-label"> remaining</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h4>Orders</h4>
          {rx.orders && rx.orders.length > 0 ? (
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Date</th>
                  <th>Product</th>
                  <th className="text-right">Quantity (T)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rx.orders.map((order, idx) => (
                  <tr key={idx}>
                    <td>{order.order_number || '-'}</td>
                    <td>{formatDateShort(order.order_date)}</td>
                    <td>{order.product_name || '-'}</td>
                    {/* A load split between overlapping prescriptions counts only
                        part here — say how much of it. */}
                    <td
                      className="text-right"
                      title={order.full_quantity != null && Math.abs(parseFloat(order.full_quantity) - parseFloat(order.quantity)) > 0.0005
                        ? 'Part of this load counts against an overlapping prescription'
                        : undefined}
                    >
                      {formatQuantity(parseFloat(order.quantity || 0))}
                      {order.full_quantity != null && Math.abs(parseFloat(order.full_quantity) - parseFloat(order.quantity)) > 0.0005 && (
                        <span className="rx-of-load"> of {formatQuantity(parseFloat(order.full_quantity))}</span>
                      )}
                      {/* The load as it was ordered, when it was packs: tonnes
                          above are worked out from it (Cam, 2026-10-13). */}
                      {order.units != null && unitSuffixFor(order.unit_weight_kg).startsWith('×') && (
                        <span className="rx-load-units">{formatCount(Math.abs(parseFloat(order.units)))} {unitSuffixFor(order.unit_weight_kg)}</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${(order.status || '').toLowerCase()}`}>
                        {order.status || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="no-data">No orders found for this prescription period</p>
          )}
        </div>

        <div className="detail-section">
          <h4>Period Summary</h4>
          {rx.monthlySummary && rx.monthlySummary.length > 0 ? (
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="text-right">Tonnes</th>
                  <th className="text-right">Expected</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rx.monthlySummary.map((period, idx) => {
                  const isOver = parseFloat(period.tonnes || 0) > parseFloat(period.expected || 0);
                  const label = period.period_start && period.period_end
                    ? formatPeriod(period.period_start, period.period_end)
                    : period.month;
                  return (
                    <tr key={idx}>
                      <td>{label}</td>
                      <td className={`text-right ${getMonthStatusClass(parseFloat(period.tonnes || 0), parseFloat(period.expected || 0))}`}>
                        {formatQuantity(parseFloat(period.tonnes || 0))}
                      </td>
                      <td className="text-right">{formatQuantity(parseFloat(period.expected || 0))}</td>
                      <td>
                        {isOver ? (
                          <span className="material-symbols-rounded" style={{ color: 'var(--color-error)', fontSize: '18px' }} title="Over expected">
                            warning
                          </span>
                        ) : (
                          <span className="material-symbols-rounded" style={{ color: 'var(--color-success)', fontSize: '18px' }} title="Within expected">
                            check_circle
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="no-data">No monthly summary available</p>
          )}
        </div>

        <div className="detail-section">
          <h4>Delivery Instructions</h4>
          {rx.delivery_instructions ? (
            <div className="notes-section">{rx.delivery_instructions}</div>
          ) : (
            <p className="no-data">No delivery instructions</p>
          )}
        </div>

        <div className="detail-section">
          <h4>Notes</h4>
          {rx.notes ? (
            <div className="notes-section">{rx.notes}</div>
          ) : (
            <p className="no-data">No notes</p>
          )}
        </div>
      </>
    );
  };

  const renderFormMode = () => {
    return (
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          <div className="form-group">
            <label>Customer</label>
            <SearchableDropdown
              value={formData.account_id}
              onChange={(value) => handleChange('account_id', value)}
              options={[
                { value: '', label: 'Select customer...' },
                ...customers.map(c => ({
                  value: c.id.toString(),
                  badge: c.account_no || ' ',
                  label: c.company_name,
                  searchText: `${c.account_no || ''} ${c.company_name}`
                }))
              ]}
              placeholder="Select customer..."
            />
          </div>

          <div className="form-group">
            <label>Product</label>
            <SearchableDropdown
              value={formData.item_id}
              onChange={(value) => handleChange('item_id', value)}
              options={[
                { value: '', label: 'Select product...' },
                ...products.map(p => ({
                  value: p.id.toString(),
                  badge: p.item_code || ' ',
                  label: p.item_name,
                  // Behind the picker's toggle. Fetched rather than filtered
                  // out server-side so a prescription that already names an
                  // archived item still shows it.
                  archived: !!p.is_archived,
                  searchText: `${p.item_code || ''} ${p.item_name}`
                }))
              ]}
              placeholder="Select product..."
            />
          </div>

          <div className="form-group">
            <label>Vet Reference</label>
            <Input
              type="text"
              value={formData.vet_reference}
              onChange={(e) => handleChange('vet_reference', e.target.value)}
              placeholder="Enter vet reference..."
            />
          </div>

          <div className="form-group">
            <label>Prescribed Tonnes</label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={formData.prescribed_tonnes}
              onChange={(e) => handleChange('prescribed_tonnes', e.target.value)}
              placeholder="Enter tonnage"
            />
          </div>

          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="form-group">
              <label>Prescription Date</label>
              <DateInput
                value={formData.from_date}
                onChange={(e) => handleChange('from_date', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Duration (months)</label>
              <Dropdown
                value={String(formData.duration_months)}
                onChange={(value) => handleChange('duration_months', parseInt(value))}
                options={[
                  { value: '1', label: '1 month' },
                  { value: '2', label: '2 months' },
                  { value: '3', label: '3 months' }
                ]}
              />
              {formData.from_date && (
                <div className="prescription-end-hint">
                  Ends {formatDateShort(computeEndDate(formData.from_date, formData.duration_months))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Delivery Instructions</label>
            <Textarea
              value={formData.delivery_instructions}
              onChange={(e) => handleChange('delivery_instructions', e.target.value)}
              placeholder="Optional delivery instructions..."
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Optional notes..."
              rows={3}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button type="submit" className="btn-slideout btn-slideout-primary" disabled={saving}>
            <div className="icon-wrapper">
              <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>
                {saving ? 'sync' : 'save'}
              </span>
            </div>
            <span className="btn-label">{saving ? 'Saving...' : (prescription ? 'Update' : 'Create')}</span>
          </button>
          <button
            data-read-safe
            type="button"
            className="btn-round btn-danger"
            onClick={mode === 'view' ? () => setCurrentMode('view') : handleClose}
            title={mode === 'view' ? 'Stop editing' : 'Cancel'}
          >
            <div className="icon-wrapper">
              <span className="material-symbols-rounded">close</span>
            </div>
            <span className="btn-label">Cancel</span>
          </button>
        </div>
      </form>
    );
  };

  const getTitle = () => {
    if (currentMode === 'view') return `Prescription ${prescription?.prescription_number || ''}`;
    if (currentMode === 'edit') return 'Edit Prescription';
    return 'New Prescription';
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content modal-lg prescription-modal ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{ paddingBottom: 'var(--spacing-lg)' }}>
          <h3 style={{ marginBottom: '0' }}>{getTitle()}</h3>
          <div className="header-actions">
            {currentMode === 'view' && prescription?.id && (
              <button
                data-write-action
                className="btn-icon"
                onClick={() => setCurrentMode('edit')}
                title="Edit prescription"
              >
                <span className="material-symbols-rounded">edit</span>
              </button>
            )}
            {(viewData?.id || prescription?.id) && (
              <button
                className="btn-icon"
                onClick={() => setShowHistory(true)}
                title="View change history"
              >
                <span className="material-symbols-rounded">history</span>
              </button>
            )}
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        {/* View mode has no footer: its only button was a second Close, and the
            header already carries one. */}
        {currentMode === 'view' ? (
          <div className="modal-body">
            {renderViewMode()}
          </div>
        ) : (
          renderFormMode()
        )}

        <AlertDialog
          isOpen={alertDialog.isOpen}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
        />

        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="prescription"
          entityId={viewData?.id || prescription?.id}
          entityIdentifier={viewData?.prescription_number || prescription?.prescription_number}
        />
      </div>
    </div>,
    document.body
  );
}

export default PrescriptionModal;
